import { create } from "zustand"
import { immer } from "zustand/middleware/immer"

import type { Role, UserProfile } from "./schemas"

/**
 * Auth state slice.
 *
 * Persistence: tokenler ve user sessionStorage'da saklanır ("ok.auth.v2").
 * localStorage YASAK (XSS'e karşı) — tab/window kapanınca clear.
 * Uygulama boot sırasında `hydrateAuth()` çağrılır.
 *
 * Dual-backend (Hems + CNI):
 *   - `primaryBackend` — login akışında ilk başarılı olan backend. /me ve
 *     refresh çağrıları default olarak buradan gider.
 *   - `secondary*` — silent dual-probe başarılı ise diğer backend'in token
 *     seti. Kullanıcı /select-panel'den ikinci paneli seçtiğinde o backend'in
 *     token'ı kullanılır. null ise tek panel.
 */

export type { Role }
export type AuthUser = UserProfile

export type LoginBackend = "hems" | "cni"

interface AuthSnapshot {
  user: AuthUser | null
  primaryBackend: LoginBackend | null
  accessToken: string | null
  refreshToken: string | null
  expiresAt: number | null
  secondaryBackend: LoginBackend | null
  secondaryAccessToken: string | null
  secondaryRefreshToken: string | null
  secondaryExpiresAt: number | null
}

interface SetSessionInput {
  user: AuthUser
  primaryBackend: LoginBackend
  accessToken: string
  refreshToken: string
  expiresIn: number
  secondary?: {
    backend: LoginBackend
    accessToken: string
    refreshToken: string
    expiresIn: number
  } | null
}

interface AuthState extends AuthSnapshot {
  setSession: (input: SetSessionInput) => void
  setTokens: (input: {
    backend: LoginBackend
    accessToken: string
    refreshToken: string
    expiresIn: number
  }) => void
  setUser: (user: AuthUser) => void
  clear: () => void
  hasRole: (role: Role) => boolean
  isAuthenticated: () => boolean
  /** Belirli bir backend için token'ları döner (primary veya secondary). */
  tokensFor: (backend: LoginBackend) => {
    accessToken: string
    refreshToken: string
    expiresAt: number
  } | null
}

const STORAGE_KEY = "ok.auth.v2"
const LEGACY_STORAGE_KEYS = ["ok.auth"] as const

const EMPTY_SNAPSHOT: AuthSnapshot = {
  user: null,
  primaryBackend: null,
  accessToken: null,
  refreshToken: null,
  expiresAt: null,
  secondaryBackend: null,
  secondaryAccessToken: null,
  secondaryRefreshToken: null,
  secondaryExpiresAt: null,
}

function persistedSnapshot(): AuthSnapshot | null {
  if (typeof window === "undefined") {
    return null
  }
  try {
    // Eski storage key varsa temizle (v1 → v2 geçişi tek seferlik).
    for (const legacy of LEGACY_STORAGE_KEYS) {
      window.sessionStorage.removeItem(legacy)
    }
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as AuthSnapshot
    if (parsed.expiresAt && parsed.expiresAt < Date.now()) {
      window.sessionStorage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function persistSnapshot(snap: AuthSnapshot) {
  if (typeof window === "undefined") {
    return
  }
  if (!snap.accessToken) {
    window.sessionStorage.removeItem(STORAGE_KEY)
    return
  }
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snap))
}

const initial: AuthSnapshot = persistedSnapshot() ?? EMPTY_SNAPSHOT

export const useAuthStore = create<AuthState>()(
  immer((set, get) => ({
    ...initial,

    setSession: (input) =>
      set((state) => {
        state.user = input.user
        state.primaryBackend = input.primaryBackend
        state.accessToken = input.accessToken
        state.refreshToken = input.refreshToken
        state.expiresAt = Date.now() + input.expiresIn * 1000
        if (input.secondary) {
          state.secondaryBackend = input.secondary.backend
          state.secondaryAccessToken = input.secondary.accessToken
          state.secondaryRefreshToken = input.secondary.refreshToken
          state.secondaryExpiresAt = Date.now() + input.secondary.expiresIn * 1000
        } else {
          state.secondaryBackend = null
          state.secondaryAccessToken = null
          state.secondaryRefreshToken = null
          state.secondaryExpiresAt = null
        }
      }),

    setTokens: (input) =>
      set((state) => {
        const expiresAt = Date.now() + input.expiresIn * 1000
        if (state.primaryBackend === input.backend) {
          state.accessToken = input.accessToken
          state.refreshToken = input.refreshToken
          state.expiresAt = expiresAt
        } else if (state.secondaryBackend === input.backend) {
          state.secondaryAccessToken = input.accessToken
          state.secondaryRefreshToken = input.refreshToken
          state.secondaryExpiresAt = expiresAt
        } else {
          // primary set edilmemiş — yeni primary olarak kabul et.
          state.primaryBackend = input.backend
          state.accessToken = input.accessToken
          state.refreshToken = input.refreshToken
          state.expiresAt = expiresAt
        }
      }),

    setUser: (user) =>
      set((state) => {
        state.user = user
      }),

    clear: () =>
      set((state) => {
        state.user = null
        state.primaryBackend = null
        state.accessToken = null
        state.refreshToken = null
        state.expiresAt = null
        state.secondaryBackend = null
        state.secondaryAccessToken = null
        state.secondaryRefreshToken = null
        state.secondaryExpiresAt = null
      }),

    hasRole: (role) => get().user?.roles.includes(role) ?? false,

    isAuthenticated: () => {
      const { accessToken, expiresAt } = get()
      if (!accessToken) {
        return false
      }
      if (expiresAt && expiresAt < Date.now()) {
        return false
      }
      return true
    },

    tokensFor: (backend) => {
      const s = get()
      if (s.primaryBackend === backend && s.accessToken && s.refreshToken && s.expiresAt) {
        return {
          accessToken: s.accessToken,
          refreshToken: s.refreshToken,
          expiresAt: s.expiresAt,
        }
      }
      if (
        s.secondaryBackend === backend &&
        s.secondaryAccessToken &&
        s.secondaryRefreshToken &&
        s.secondaryExpiresAt
      ) {
        return {
          accessToken: s.secondaryAccessToken,
          refreshToken: s.secondaryRefreshToken,
          expiresAt: s.secondaryExpiresAt,
        }
      }
      return null
    },
  })),
)

useAuthStore.subscribe((state) => {
  persistSnapshot({
    user: state.user,
    primaryBackend: state.primaryBackend,
    accessToken: state.accessToken,
    refreshToken: state.refreshToken,
    expiresAt: state.expiresAt,
    secondaryBackend: state.secondaryBackend,
    secondaryAccessToken: state.secondaryAccessToken,
    secondaryRefreshToken: state.secondaryRefreshToken,
    secondaryExpiresAt: state.secondaryExpiresAt,
  })
})

/**
 * Boot sırasında çağrılır (main.tsx). Şu anda sadece no-op; persisted snapshot
 * zaten modül yüklenirken initial state'e yedi.
 */
export function hydrateAuth(): void {
  // placeholder
}
